import { useMemo } from 'react';
import { Home } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useLandingOptions } from '@/features/nav';

/**
 * Start-page picker.
 *
 * This used to be seven hardcoded options with no permission check, so a user
 * could pick a page they weren't allowed to open — and then get redirected
 * straight to /forbidden on every cold boot. Options now come from the nav
 * registry filtered by the caller's own grants, so an unusable choice isn't
 * offered in the first place.
 */
export function LandingPagePicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const options = useLandingOptions();

  const grouped = useMemo(() => {
    const buckets = new Map<string, typeof options>();
    for (const option of options) {
      const arr = buckets.get(option.group) ?? [];
      arr.push(option);
      buckets.set(option.group, arr);
    }
    return Array.from(buckets.entries());
  }, [options]);

  const current = value ?? '/';
  // A stored value can outlive the grant that justified it. Keep showing it so
  // the control isn't blank while the router repairs the preference.
  const isOrphan = current !== '/' && !options.some((o) => o.path === current);

  return (
    <Select value={current} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-52">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {isOrphan ? (
          <SelectItem value={current}>
            <span className="text-muted-foreground">{current} (no access)</span>
          </SelectItem>
        ) : null}
        {grouped.map(([group, items]) => (
          <SelectGroup key={group}>
            <SelectLabel>{group}</SelectLabel>
            {items.map((option) => {
              const Icon = option.path === '/' ? Home : option.icon;
              return (
                <SelectItem key={option.path} value={option.path}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon className="h-3 w-3" aria-hidden />
                    {option.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
